import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import Progress from 'pf9-ui-components/built/components/progress/Progress'
import QuantitySelector from 'pf9-ui-components/built/components/QuantitySelector'
import DropdownField from 'pf9-ui-components/built/components/validatedForm/DropdownField'
import TextField from 'pf9-ui-components/built/components/validatedForm/TextField'
import ToggleSwitchField from 'pf9-ui-components/built/components/validatedForm/ToggleSwitchField'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Text from 'pf9-ui-components/built/elements/Text'
import { prop } from 'ramda'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import ApiClient from 'api-client/ApiClient'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import { humanReadableSize } from 'app/plugins/openstack/helpers'
import { type RootState, useAppSelector } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import Dropdown from 'core/elements/dropdown'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { type SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import type Theme from 'core/themes/model'
import { validators } from 'core/utils/fieldValidators'
import DataKeys from 'k8s/DataKeys'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import { listKaapiClusters, updateNodePools } from '../kaapi/clusters/actions'

const { kubernetes } = ApiClient.getInstance()

export enum NodeUpdateStrategyTypes {
  Count = 'count',
  Percentage = 'percent',
}
const rollingUpdateTypeOptions = [
  { label: 'Count', value: NodeUpdateStrategyTypes.Count },
  { label: 'Percentage', value: NodeUpdateStrategyTypes.Percentage },
]

const convertToPercentageStr = (value: string | number) => {
  if (!value) return value
  return value.toString() + '%'
}

//TODO: Need to refactor as its not working with getParamsUpdater
// function parsePercentageValue(value) {
//   if (typeof value === 'string' && value.includes('%')) {
//     return parseFloat(value.replace('%', ''))
//   } else if (typeof value === 'number') {
//     return value
//   }
//   return null // Default case for invalid values
// }

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    display: 'grid',
    gridGap: theme.spacing(4),
  },
  section: {
    paddingBottom: theme.spacing(4),
    marginBottom: theme.spacing(4),
    borderBottom: `solid 1px ${theme.components.card.border}`,
  },
  maxNodesText: {
    color: theme.palette.text.primary,
    cursor: 'pointer',
  },
  maxNodesTextDisabled: {
    color: theme.palette.text.disabled,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  nodesSelector: {
    cursor: 'pointer',
    marginTop: theme.spacing(1),
  },
  nodesSelectorDisabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  autoScalingSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  maxNodesSelector: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  maxNodesSelectorEnabled: {
    cursor: 'pointer',
    pointerEvents: 'auto',
  },
}))

interface Props {
  rows: any[]
  selectedNodeIndex?: number
  onClose: () => void
  isClusterClass: boolean
  onUpgradeScreen?: boolean
  clusterName: string
}

const getValidationRules = (
  type: string,
  params: { maxSurgeValue: number; maxUnavailableValue: number; nodes: number },
) => {
  const bothZero = params.maxSurgeValue === 0 && params.maxUnavailableValue === 0
  const maxRange = type === 'percent' ? 100 : params.nodes

  return [
    bothZero
      ? validators.rangeValueWithLable(
          params.maxSurgeValue,
          params.maxUnavailableValue,
          'Max Surge Value',
          'Max Unavailable Value',
        )
      : validators.rangeValue(0, maxRange),
  ]
}

export default function EditClusterNodeGroupsModal({
  rows: [nodeGroupDetails],
  selectedNodeIndex,
  isClusterClass,
  onClose,
  onUpgradeScreen = false,
  clusterName,
}: Props) {
  const { activeKaapiTenant: namespace } = useSelector<RootState, SessionState>(
    prop(sessionStoreKey),
  )
  const defaultParams = {
    name: nodeGroupDetails?.name,
    nodes: nodeGroupDetails?.nodes,
    strategy: nodeGroupDetails?.strategy,
    replicas: nodeGroupDetails?.spec?.replicas,
    autoscalingEnabled: nodeGroupDetails?.spec?.autoscaling === 'Enabled',
    autoscalingMaxNodes: nodeGroupDetails?.spec?.autoscalingMaxNodes || 1,
    autoscalingMinNodes: nodeGroupDetails?.spec?.autoscalingMinNodes || 1,
    maxSurgeType: nodeGroupDetails?.maxSurge,
    maxSurgeValue:
      typeof nodeGroupDetails?.maxSurgeValue === 'string' &&
      nodeGroupDetails?.maxSurgeValue?.includes('%')
        ? parseFloat(nodeGroupDetails?.maxSurgeValue?.replace('%', ''))
        : typeof nodeGroupDetails?.maxSurgeValue === 'number'
          ? nodeGroupDetails?.maxSurgeValue
          : null,
    maxUnavailableType: nodeGroupDetails?.maxUnavailable,
    maxUnavailableValue:
      typeof nodeGroupDetails?.maxUnavailableValue === 'string' &&
      nodeGroupDetails?.maxUnavailableValue?.includes('%')
        ? parseFloat(nodeGroupDetails?.maxUnavailableValue?.replace('%', ''))
        : typeof nodeGroupDetails?.maxUnavailableValue === 'number'
          ? nodeGroupDetails?.maxUnavailableValue
          : null,
  }

  const [maxSurgeType, setMaxSurgeType] = useState(
    defaultParams?.maxSurgeType?.toLowerCase() || NodeUpdateStrategyTypes.Count,
  )
  const [maxUnavailableType, setMaxUnavailableType] = useState(
    defaultParams?.maxUnavailableType?.toLowerCase() || NodeUpdateStrategyTypes.Count,
  )
  const [autoscalingError, setAutoscalingError] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const {
    progressDetails,
    updateProgress,
    resetProgress: resetClusterProgress,
  } = useClusterProgress({
    initLabel: 'Updating Cluster',
  })

  const { params, getParamsUpdater, setParams, updateParams } = useParams(defaultParams)
  const {
    update: updateNodePool,
    updating: updatingNodePools,
    error: updateNodePoolsError,
  } = useUpdateAction(updateNodePools)
  const { reload: reloadKaapiClusters } = useListAction(listKaapiClusters)

  const classes = useStyles()

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors)
  const vmFlavors = useAppSelector(flavorsSelector)

  const nodegroupFlavor = vmFlavors?.find(
    (vmFlavor) =>
      vmFlavor?.name === nodeGroupDetails?.openStackMachineTemplate?.spec?.template?.spec?.flavor,
  )

  const submitForm = useCallback(
    async (values) => {
      setShowProgress(true)
      setLoading(true)

      const editBodyWithTopology: any = [
        {
          op: 'add',
          path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/strategy`,
          value: {
            type: values.nodesUpdateStrategy,
            rollingUpdate: {
              maxSurge:
                values.maxSurgeType === 'percent'
                  ? convertToPercentageStr(values.maxSurgeValue)
                  : values.maxSurgeValue,
              maxUnavailable:
                values.maxUnavailableType === 'percent'
                  ? convertToPercentageStr(values.maxUnavailableValue)
                  : values.maxUnavailableValue,
            },
          },
        },
      ]

      if (!onUpgradeScreen) {
        // Update Relicas
        if (isClusterClass && !params.autoscalingEnabled) {
          editBodyWithTopology.push({
            op: 'replace',
            path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/replicas`,
            value: Number(params.replicas),
          })
        }

        // Enabling autoscaling
        if (params.autoscalingEnabled) {
          if (isClusterClass) {
            if (nodeGroupDetails?.spec?.autoscaling === 'Enabled') {
              editBodyWithTopology.push(
                {
                  op: 'replace',
                  path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/metadata/annotations/cluster.x-k8s.io~1cluster-api-autoscaler-node-group-min-size`,
                  value: params.autoscalingMinNodes.toString(),
                },
                {
                  op: 'replace',
                  path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/metadata/annotations/cluster.x-k8s.io~1cluster-api-autoscaler-node-group-max-size`,
                  value: params.autoscalingMaxNodes.toString(),
                },
              )
            } else {
              editBodyWithTopology.push(
                {
                  op: 'remove',
                  path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/replicas`,
                },
                {
                  op: 'add',
                  path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/metadata/annotations`,
                  value: {
                    'cluster.x-k8s.io/cluster-api-autoscaler-node-group-min-size':
                      params.autoscalingMinNodes.toString(),
                    'cluster.x-k8s.io/cluster-api-autoscaler-node-group-max-size':
                      params.autoscalingMaxNodes.toString(),
                  },
                },
              )
            }
          }
        }

        // Disable autosacaling
        if (nodeGroupDetails?.spec?.autoscaling === 'Enabled' && !params.autoscalingEnabled) {
          if (isClusterClass) {
            editBodyWithTopology.push(
              {
                op: 'remove',
                path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/metadata/annotations/cluster.x-k8s.io~1cluster-api-autoscaler-node-group-min-size`,
              },
              {
                op: 'remove',
                path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/metadata/annotations/cluster.x-k8s.io~1cluster-api-autoscaler-node-group-max-size`,
              },
              {
                op: 'replace',
                path: `/spec/topology/workers/machineDeployments/${selectedNodeIndex}/replicas`,
                value: params.replicas,
              },
            )
          }
        }
      }
      updateProgress(ProgressStep.Init, ProgressStatus.Success)

      if (isClusterClass) {
        try {
          const { success } = await updateNodePool({
            namespace,
            body: editBodyWithTopology,
            clusterName,
          })

          if (success) {
            updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
            let isNodeGroupUpdated = false
            const maxAttempts = 60 // 5 minutes timeout (60 * 5 seconds)
            let attempts = 0

            while (!isNodeGroupUpdated && attempts < maxAttempts) {
              attempts++
              const machineDeployments = await kubernetes.getAllCustomResources(
                DataKeys.KaapiMachineDeployments,
              )
              const nodeGroupUpdated = machineDeployments?.find(
                (md) => md?.metadata?.name === params.name,
              )

              if (
                nodeGroupUpdated?.metadata?.generation !== nodeGroupDetails?.metadata?.generation
              ) {
                isNodeGroupUpdated = true
                updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
                reloadKaapiClusters(true)
                setIsProgressComplete(true)
                setLoading(false)
              } else {
                await waitSeconds(5)
              }
            }
            if (!isNodeGroupUpdated) {
              setLoading(false)
              throw new Error('Node groups are taking too long to get deployed')
            }
          } else {
            setLoading(false)
            setErrorMessage({ message: 'Failed to update node group' })
            updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
          }
        } catch (err) {
          setLoading(false)
          setErrorMessage(err || { message: 'Failed to update node group' })
          updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
          console.error(err)
          return
        }
      } else {
        setLoading(false)
        setShowProgress(false)
        setErrorMessage({ message: 'This cluster does not support clusterclass.' })
      }
    },
    [params, nodeGroupDetails, onUpgradeScreen, isClusterClass, selectedNodeIndex, clusterName],
  )

  const handleClose = useCallback(() => {
    setParams(defaultParams)
    setIsProgressComplete(false)
    setShowProgress(false)
    onClose()
  }, [setParams, defaultParams, onClose])

  const resetProgress = useCallback(() => {
    setIsProgressComplete(false)
    setShowProgress(false)
    resetClusterProgress()
  }, [resetClusterProgress])

  useEffect(() => {
    if (!params?.autoscalingEnabled) {
      setAutoscalingError('')
      return
    }
    const errorMessage =
      params?.autoscalingMaxNodes < params?.replicas
        ? 'Max nodes must be greater than or equal to the number of desired nodes.'
        : params?.autoscalingMaxNodes < params?.autoscalingMinNodes
          ? 'Max nodes must be greater than min nodes.'
          : ''
    setAutoscalingError(errorMessage)
  }, [
    params?.autoscalingMaxNodes,
    params?.replicas,
    params?.autoscalingEnabled,
    params?.autoscalingMinNodes,
  ])

  const editNodeGroupError = updateNodePoolsError || errorMessage

  return (
    <ModalForm
      open
      title={`Edit`}
      entityName={params.name}
      onSubmit={showProgress || isProgressComplete ? handleClose : submitForm}
      onClose={handleClose}
      submitting={updatingNodePools || loading}
      error={editNodeGroupError}
      submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Save Changes'}
      disableSubmit={!!autoscalingError}
      maxWidth={464}
      onBackButtonClick={resetProgress}
      showBackButton={editNodeGroupError?.message && showProgress}
      data-testid="edit-cluster-node-group-modal"
    >
      {!showProgress && !isProgressComplete ? (
        <div>
          {/* Hide the below field for k8s upgrade screens */}
          {!onUpgradeScreen && (
            <div>
              <ServiceUnhealthyInfo
                action="Editing node group"
                hypervisor
                imageLibrary
                blockStorage
                network
              />
              <div className={classes.section}>
                <Progress loading={loadingVmFlavors}>
                  <Text variant="body1">
                    <strong>{nodegroupFlavor?.name}</strong>
                  </Text>
                  <Text variant="body2">
                    (vCPUs: <strong>{nodegroupFlavor?.vcpus} cores</strong> / Memory:{' '}
                    <strong>
                      {humanReadableSize(nodegroupFlavor?.ram * 1024 * 1024 * 1024, false)} GiB
                    </strong>{' '}
                    / Storage:{' '}
                    <strong>
                      {humanReadableSize(nodegroupFlavor?.disk * 1024 * 1024 * 1024, false)} GiB
                    </strong>
                    )
                  </Text>
                  <br />
                  <Text variant="body2">Desired Nodes</Text>
                  <QuantitySelector
                    id="replica"
                    value={params.replicas}
                    onChange={(value) => updateParams({ replicas: value })}
                    min={1}
                    data-testid="replica-selector"
                    disabled={params.autoscalingEnabled}
                  />
                </Progress>
              </div>
              <div className={classes.section}>
                <ToggleSwitchField
                  id="autoscaling"
                  label="Automatically scale this Node Group"
                  value={params.autoscalingEnabled}
                  onChange={(value) => updateParams({ autoscalingEnabled: value })}
                  data-testid="autoscaling-toggle"
                />
                <br />
                <div className={classes.autoScalingSection}>
                  <div>
                    <Text variant="body2">Min Nodes</Text>
                    <QuantitySelector
                      id="autoscalingMinNodes"
                      disabled={!params.autoscalingEnabled}
                      value={params.autoscalingMinNodes}
                      onChange={(value) => updateParams({ autoscalingMinNodes: value })}
                      className={clsx(classes.nodesSelector, {
                        [classes.nodesSelectorDisabled]: !params.autoscalingEnabled,
                      })}
                      min={1}
                      data-testid="min-nodes-selector"
                    />
                  </div>
                  <div>
                    <Text variant="body2">Max Nodes</Text>
                    <QuantitySelector
                      id="autoscalingMaxNodes"
                      disabled={!params.autoscalingEnabled}
                      value={params.autoscalingMaxNodes}
                      onChange={(value) => updateParams({ autoscalingMaxNodes: value })}
                      className={clsx(classes.nodesSelector, {
                        [classes.nodesSelectorDisabled]: !params.autoscalingEnabled,
                      })}
                      min={params.replicas}
                      data-testid="max-nodes-selector"
                    />
                  </div>
                </div>
                {autoscalingError && <ErrorMessage>{autoscalingError}</ErrorMessage>}
              </div>
            </div>
          )}

          <div className={classes.fields}>
            <DropdownField
              DropdownComponent={Dropdown}
              id="nodesUpdateStrategy"
              label="Strategy"
              value="RollingUpdate"
              disabled
              items={[{ label: 'Rolling Update', value: 'RollingUpdate' }]}
              info={
                <>
                  A <b>rolling update</b> allows a Node Group update to take place with zero or
                  controlled number of nodes to be down.
                </>
              }
              data-testid="update-strategy-dropdown"
            />
            <DropdownField
              DropdownComponent={Dropdown}
              id="maxSurgeType"
              label="Max Surge Type"
              value={maxSurgeType}
              items={rollingUpdateTypeOptions}
              onChange={(value) => {
                setMaxSurgeType(value)
              }}
              info={
                <>
                  The maximum number of machines that can be scheduled above the desired number of
                  nodes.
                  <br /> Value can be an absolute number (ex: 5) or a percentage of desired Nodes
                  (ex: 10%).
                  <br /> This can not be 0 if MaxUnavailable is 0.
                  <br /> Absolute number is calculated from percentage by rounding up.
                  <br /> Defaults to 1.
                </>
              }
              data-testid="max-surge-type-dropdown"
            />
            <TextField
              crossOrigin={undefined}
              id="maxSurgeValue"
              label="Max Surge Value"
              onChange={getParamsUpdater('maxSurgeValue')}
              value={params.maxSurgeValue}
              type="number"
              required
              icon={maxSurgeType === 'percent' ? 'percent' : ''}
              iconProps={{ placement: 'end' }}
              validations={getValidationRules(maxSurgeType, params)}
              info={
                <div>
                  Maximum number/percentage of nodes that can be scaled up above the desired Node
                  Group replica count.
                  <br /> Applicable values:
                  <br /> Minimum = 0<br /> Maximum = Any number / percentage
                  <br /> Default = 0
                </div>
              }
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              data-testid="max-surge-value-input"
            />
            <DropdownField
              DropdownComponent={Dropdown}
              id="maxUnavailableType"
              label="Max Unavailable Type"
              value={maxUnavailableType}
              items={rollingUpdateTypeOptions}
              onChange={(value) => {
                setMaxUnavailableType(value)
              }}
              info={
                <>
                  The maximum number of nodes that can be unavailable during the update.
                  <br /> Value can be an absolute number (ex: 5) or a percentage of desired nodes
                  (ex: 10%).
                  <br /> Absolute number is calculated from percentage by rounding down.
                  <br /> This cannot be 0 if MaxSurge is 0.
                  <br /> Defaults to 0.
                </>
              }
              data-testid="max-unavailable-type-dropdown"
            />
            <TextField
              crossOrigin={undefined}
              id="maxUnavailableValue"
              label="Max Unavailable Value"
              onChange={getParamsUpdater('maxUnavailableValue')}
              value={params.maxUnavailableValue}
              type="number"
              icon={maxUnavailableType === 'percent' ? 'percent' : ''}
              iconProps={{ placement: 'end' }}
              required
              validations={getValidationRules(maxUnavailableType, params)}
              info={
                <div>
                  Maximum number/percentage of nodes that can unavailable.
                  <br /> Applicable values:
                  <br /> Minimum = 0<br /> Maximum = Node group replica count / 100 %<br /> Default
                  = 0
                </div>
              }
              nonce={undefined}
              onResize={undefined}
              onResizeCapture={undefined}
              enterKeyHint={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              data-testid="max-unavailable-value-input"
            />
          </div>
        </div>
      ) : (
        <StepProgress
          progressDetails={progressDetails}
          title={`Node group update is in progress. 
              Please wait and do not close this dialog, or the update will be cancelled.`}
        />
      )}
    </ModalForm>
  )
}
